import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, BookMarked, Sunrise, Book, Sparkles, Compass, HeartHandshake, Quote } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import DevotionalResponse from "@/components/DevotionalResponse";
import { api } from "@/lib/api";

const FUNCTION_LABEL = {
  fundamento: "Fundamento",
  desenvolvimento: "Desenvolvimento",
  aplicacao: "Aplicação",
};

const FUNCTION_ACCENT = {
  fundamento: "border-[#0B1E36] text-[#0B1E36]",
  desenvolvimento: "border-[#C5A059] text-[#C5A059]",
  aplicacao: "border-[#7B3F00] text-[#7B3F00]",
};

const BLOCK_META = [
  { key: "abertura", label: "Abertura", icon: Sunrise },
  { key: "verdade", label: "Verdade", icon: Sparkles },
  { key: "hoje", label: "Hoje", icon: Compass },
  { key: "oracao", label: "Oração", icon: HeartHandshake },
  { key: "frase_compartilhar", label: "Para levar consigo", icon: Quote },
];

// Detecta se `id` é um UUID legacy (tem hífens no formato UUID)
function isUuidLike(id) {
  if (!id) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
}

export default function Devocional() {
  const { id } = useParams();
  const [dev, setDev] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [mode, setMode] = useState("novo"); // "novo" | "legacy"

  useEffect(() => {
    setLoading(true);
    setErr(null);
    const useLegacy = isUuidLike(id);
    setMode(useLegacy ? "legacy" : "novo");
    const url = useLegacy ? `/devotional/${id}` : `/today`;
    api
      .get(url)
      .then(({ data }) => setDev(data))
      .catch(() => setErr("Não foi possível carregar o devocional."))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div data-testid="devotional-page" className="bg-white min-h-screen">
      <Navbar />
      <main className="pt-28 pb-24">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <Link
            to="/"
            data-testid="devotional-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#111827]/70 hover:text-[#C5A059] transition-colors duration-300"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            Voltar ao início
          </Link>

          {loading && (
            <div className="mt-16 space-y-6 animate-pulse" data-testid="devotional-loading">
              <div className="h-10 bg-[#F5F7FA] w-3/4" />
              <div className="h-4 bg-[#F5F7FA] w-1/2" />
              <div className="h-4 bg-[#F5F7FA] w-full" />
              <div className="h-4 bg-[#F5F7FA] w-11/12" />
            </div>
          )}

          {!loading && err && (
            <div className="mt-24 text-center text-[#111827]/70" data-testid="devotional-error">{err}</div>
          )}

          {!loading && dev && mode === "novo" && (
            <DevocionalPastoral dev={dev} />
          )}

          {!loading && dev && mode === "legacy" && (
            <DevocionalLegacy dev={dev} />
          )}
        </div>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

// ─── Devocional novo (pastoral_blocks + scripture_connections) ─────────────
function DevocionalPastoral({ dev }) {
  const pb = dev.pastoral_blocks || {};
  const palavra = pb.palavra || dev.central_verse || {};
  const conns = dev.scripture_connections || [];
  const dateFormatted = (dev.date || "").split("-").reverse().join("/");
  const title = pb.abertura ? extractTitle(pb.abertura) : "Devocional de Hoje";

  return (
    <article data-testid="devotional-article" className="mt-14">
      <p className="text-[0.72rem] uppercase tracking-[0.3em] text-[#C5A059]">
        Devocional Diário · Carlos Medeiros{dateFormatted ? ` · ${dateFormatted}` : ""}
      </p>

      <h1 className="mt-6 font-serif-display text-[2.4rem] sm:text-4xl md:text-5xl lg:text-[3.75rem] text-[#0B1E36] leading-[1.05] tracking-[-0.028em] font-medium" data-testid="devotional-title">
        {title}
      </h1>

      {/* Palavra do dia — versículo central */}
      {palavra.text && (
        <div className="mt-10 border-l-2 border-[#C5A059] pl-6 py-2" data-testid="devotional-palavra">
          <p className="font-serif-display italic text-[1.35rem] md:text-[1.55rem] text-[#0B1E36]/85 leading-[1.55] tracking-[-0.01em]">
            &ldquo;{palavra.text}&rdquo;
          </p>
          <p className="mt-4 text-xs uppercase tracking-[0.28em] text-[#C5A059] font-medium">
            {palavra.reference}
          </p>
        </div>
      )}

      {/* Blocos pastorais */}
      <div className="mt-16 space-y-16">
        {BLOCK_META.filter((b) => pb[b.key]).map(({ key, label, icon: Icon }) => {
          if (key === "abertura") return null; // já usada no title
          const value = pb[key];
          const text = typeof value === "string" ? value : "";
          return (
            <section key={key} data-testid={`devotional-block-${key}`}>
              <div className="flex items-center gap-3">
                <Icon size={18} strokeWidth={1.5} className="text-[#C5A059]" />
                <h2 className="eyebrow">{label}</h2>
              </div>
              <p className={`mt-6 ${key === "frase_compartilhar" ? "font-serif-display italic text-[1.55rem] md:text-[1.85rem] text-[#0B1E36] leading-[1.35] tracking-[-0.014em]" : "devotional-prose"}`}>
                {text}
              </p>
            </section>
          );
        })}

        {/* Bloco Abertura (contexto que virou intro do devocional) */}
        {pb.abertura && (
          <section data-testid="devotional-block-contexto">
            <div className="flex items-center gap-3">
              <Book size={18} strokeWidth={1.5} className="text-[#C5A059]" />
              <h2 className="eyebrow">Contexto Pastoral</h2>
            </div>
            <p className="mt-6 devotional-prose">
              {pb.abertura}
            </p>
          </section>
        )}
      </div>

      {/* Conexões bíblicas (scripture_connections) */}
      {conns.length > 0 && (
        <section className="mt-24" data-testid="devotional-connections">
          <div className="flex items-center gap-3">
            <BookMarked size={18} strokeWidth={1.5} className="text-[#C5A059]" />
            <h2 className="eyebrow">Passagens que Ecoam</h2>
          </div>
          <div className="mt-8 space-y-6">
            {conns.map((c, i) => {
              const fn = (c.theological_function || "").toLowerCase();
              const accent = FUNCTION_ACCENT[fn] || "border-[#C5A059] text-[#C5A059]";
              return (
                <div
                  key={i}
                  data-testid={`devotional-connection-${i}`}
                  className={`border-l-2 pl-5 py-3 ${accent}`}
                >
                  <p className="text-[0.68rem] uppercase tracking-[0.28em] font-medium">
                    {FUNCTION_LABEL[fn] || "Referência"}
                  </p>
                  <p className="mt-2 font-serif-display italic text-[1.05rem] md:text-[1.15rem] text-[#0B1E36]/85 leading-[1.55]">
                    &ldquo;{c.verse_text}&rdquo;
                  </p>
                  <p className="mt-3 text-xs uppercase tracking-[0.22em] text-[#0B1E36]/70">
                    {c.reference}
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="mt-24 flex flex-col sm:flex-row gap-4">
        <Link
          to="/devocional/arquivo"
          data-testid="devotional-archive-link"
          className="btn-secondary-dark"
        >
          Ver arquivo
          <ArrowUpRight size={14} strokeWidth={1.5} />
        </Link>
        <Link to="/semana" data-testid="devotional-semana-link" className="btn-primary-gold">
          Ir para a semana toda
        </Link>
      </div>

      {dev.id && <DevotionalResponse devotionalId={dev.id} />}
    </article>
  );
}

// Extrai um título curto da primeira frase de "abertura"
function extractTitle(abertura) {
  if (!abertura) return "Devocional de Hoje";
  const primeira = abertura.split(".")[0].trim();
  if (primeira.length > 6 && primeira.length <= 80) return primeira + ".";
  return "Devocional de Hoje";
}

// ─── Devocional legacy (mantido para /devocional/<uuid>) ───────────────────
function DevocionalLegacy({ dev }) {
  return (
    <article data-testid="devotional-article-legacy" className="mt-14">
      <p className="text-[0.72rem] uppercase tracking-[0.3em] text-[#C5A059]">
        Devocional Diário · Carlos Medeiros
      </p>
      <h1 className="mt-6 font-serif-display text-[2.75rem] sm:text-5xl md:text-6xl lg:text-[5rem] text-[#0B1E36] leading-[1.0] tracking-[-0.032em] font-medium">
        {dev.title}
      </h1>
      <div className="mt-8 border-l-2 border-[#C5A059] pl-6 py-2">
        <p className="font-serif-display italic text-[1.4rem] md:text-[1.65rem] text-[#0B1E36]/85 leading-[1.55] tracking-[-0.01em]">
          &ldquo;{dev.verse_text}&rdquo;
        </p>
        <p className="mt-4 text-xs uppercase tracking-[0.28em] text-[#C5A059] font-medium">
          {dev.verse_reference}
        </p>
      </div>
      <h2 className="mt-16 eyebrow">Reflexão</h2>
      <div className="mt-6 drop-cap devotional-prose">
        {(dev.reflection || "").split("\n").map((p, i) => (
          <p key={i} className={i === 0 ? "" : "mt-9"}>{p}</p>
        ))}
      </div>
      <div className="my-20 flex items-center gap-6">
        <span className="flex-1 h-px bg-[#E5E7EB]" />
        <span className="font-serif-display text-xl text-[#C5A059] italic">✦</span>
        <span className="flex-1 h-px bg-[#E5E7EB]" />
      </div>
      <h2 className="eyebrow">Aplicação Prática</h2>
      <p className="mt-6 devotional-prose">{dev.application}</p>
      <div className="my-20 flex items-center gap-6">
        <span className="flex-1 h-px bg-[#E5E7EB]" />
        <span className="font-serif-display text-xl text-[#C5A059] italic">✦</span>
        <span className="flex-1 h-px bg-[#E5E7EB]" />
      </div>
      <h2 className="eyebrow">Oração Guiada</h2>
      <p className="mt-6 serif-body">{dev.prayer}</p>
      <div className="mt-24 py-14 border-y border-[#E5E7EB] text-center">
        <BookMarked size={22} strokeWidth={1.5} className="text-[#C5A059] mx-auto" />
        <p className="mt-8 font-serif-display text-[1.75rem] md:text-[2.15rem] text-[#0B1E36] italic leading-[1.32] tracking-[-0.018em] max-w-xl mx-auto">
          &ldquo;{dev.impact_phrase}&rdquo;
        </p>
      </div>
      <div className="mt-12 flex flex-col sm:flex-row gap-4">
        <Link
          to="/devocional/arquivo"
          data-testid="devotional-archive-link"
          className="btn-secondary-dark"
        >
          Ver arquivo
          <ArrowUpRight size={14} strokeWidth={1.5} />
        </Link>
        <Link to="/#livros" className="btn-primary-gold">
          Conhecer os livros
        </Link>
      </div>
      <DevotionalResponse devotionalId={dev.id} />
    </article>
  );
}
